import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { FIELD_LABEL_CLASS } from "./section-header";

/** Canon `.card` / `.find`: rounded-md, hairline, panel-2, 8x10, gap 6. tone marks provenance (AI / reserve). */
export const cardVariants = cva("flex min-w-0 flex-col gap-1.5 rounded-md border bg-panel-2 py-2", {
  variants: {
    /** `sidebar`: a card in a sidebar panel — its text on the panel's text line (box at 8, 1 + 5 inside → 14, index.css). */
    inset: { panel: "px-2.5", sidebar: "sb-card" },
    tone: {
      plain: "border-line",
      ai: "border-ai/40",
      reserve: "border-reserve/40",
      warn: "border-warn/40",
    },
    undone: { true: "opacity-55", false: "" },
  },
  defaultVariants: { tone: "plain", undone: false, inset: "panel" },
});

export interface CardProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof cardVariants> {}

export const Card = React.forwardRef<HTMLDivElement, CardProps>(({ tone, undone, inset, className, ...props }, ref) => (
  <div ref={ref} className={cn(cardVariants({ tone, undone, inset }), className)} {...props} />
));
Card.displayName = "Card";

/** The class string of a Note, for blocks that cannot be a <Note> element (form groups, legacy wrappers). */
export const NOTE_CLASS = "flex min-w-0 flex-col gap-1 rounded-md border border-line bg-panel-2 px-2.5 py-2 text-body-s";

/** Canon `.note`: a card with an optional dim caps "who sees it" line (`who`), 13px body.
 *  `inner` = a note that lives inside an objective: no frame, a left stripe in the "own" colour.
 *  ONE look for every note and every "for the GM" block; `gm` dims the body (GM-only text). */

export interface NoteProps extends React.HTMLAttributes<HTMLDivElement> {
  who?: React.ReactNode;
  inner?: boolean;
  gm?: boolean;
}
export const Note = React.forwardRef<HTMLDivElement, NoteProps>(({ who, inner, gm, className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex min-w-0 flex-col gap-1 text-body-s",
      inner
        ? "rounded-r border-l-2 border-own bg-own/12 px-2.5 py-2"
        : "rounded-md border border-line bg-panel-2 px-2.5 py-2",
      gm ? "text-muted-foreground" : "text-fg",
      className,
    )}
    {...props}
  >
    {who ? <div className={FIELD_LABEL_CLASS}>{who}</div> : null}
    {children}
  </div>
));
Note.displayName = "Note";
